import {
	useCallback,
	useEffect,
	useLayoutEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import DailyIframe from '@daily-co/daily-js';
import type {
	DailyCall,
	DailyEventObjectTranscriptionMessage,
} from '@daily-co/daily-js';
import { useQueryClient } from '@tanstack/react-query';
import { ConfirmActionModal } from '@/components/ui/ConfirmActionModal';
import { SessionCallProntuarioPanel } from '@/pages/professional/sessao-chamada/components';
import { SessionEndTransition } from '@/pages/professional/sessao-chamada/components/prontuario/session-end-transition';
import { SessionEndProntuarioReview } from '@/pages/professional/sessao-chamada/components/prontuario/session-end-review/SessionEndProntuarioReview';
import { SessionEndCallSuccess } from '@/pages/professional/sessao-chamada/components/prontuario/session-end-review/SessionEndCallSuccess';
import {
	MedicalRecordService,
	SessionService,
	useGetSession,
	usePatchSessionEnd,
} from '@/services';
import { peekProntuarioSessionNotes } from '@/pages/professional/sessao-chamada/utils/prontuarioSessionNotesStorage';
import { resolveSessionParticipantTarget } from '@/pages/professional/sessao-chamada/utils/prontuarioPatientSummary';
import { cn } from '@/lib/utils';
import {
	isSessionCallAccessState,
	isSessionEligibleForCallProntuario,
	isSessionTranscriptionAllowed,
	isSessionTranscriptionMock,
	PRONTUARIO_NO_TRANSCRIPTION_BADGE_HINT,
	PRONTUARIO_NO_TRANSCRIPTION_HEADER_HINT,
	PRONTUARIO_LIVE_CAPTURE_STOPPED_NOTICE,
	PRONTUARIO_TRANSCRIPTION_BADGE_HINT,
	PRONTUARIO_TRANSCRIPTION_HEADER_HINT,
	shouldShowProfessionalEndSession,
	shouldShowSessionCallProntuario,
} from '@/constants/sessionCall';
import {
	buildProfessionalTrayButtons,
	PLUMAA_END_SESSION_TRAY_BUTTON_ID,
	PLUMAA_LIVE_CAPTIONS_TRAY_BUTTON_ID,
	PLUMAA_PRONTUARIO_TRAY_BUTTON_ID,
	SESSION_CALL_TRAY_CUSTOM_CSS,
} from '@/constants/sessionCallDaily';
import type { SessionCallPageShellProps } from '@/components/session-call/SessionCallPageShell.custom';
import { SessionCallLiveCaptionsOverlay } from '@/components/session-call/SessionCallLiveCaptionsOverlay';
import { SessionCallResizableSplit } from '@/components/session-call/SessionCallResizableSplit';
import {
	appendTranscriptionChatMessage,
	createTranscriptionChatMessage,
	ensureDailyCallTranscriptionStarted,
	finalizeLiveTranscriptionChatMessages,
	TRANSCRIPTION_CHAT_GROUP_PAUSE_MS,
	type SessionCallTranscriptionChatMessage,
} from '@/components/session-call/sessionCallTranscriptionChat';
import type { SessionCallProntuarioBadgeHint } from '@/pages/professional/sessao-chamada/components/prontuario/SessionCallProntuarioRecordingBadge';
import { PRONTUARIO_BG_CLASS } from '@/pages/professional/sessao-chamada/constants/prontuarioTheme';
import {
	formatTimeUntil,
	isSessionCallPeriodElapsed,
	isSessionCallRetryableErrorKind,
	resolveSessionCallJoinError,
	SESSION_CALL_EXPIRED_MESSAGE,
	shouldHandleCallJoinError,
	type SessionCallJoinErrorKind,
} from '@/components/session-call/sessionCallJoinUtils';
import {
	buildDailyJoinProperties,
	resolveVideoRoomApiError,
	SESSION_CALL_VIDEO_ROOM_RETRY_MS,
} from '@/components/session-call/sessionCallDailyJoin';

const PRONTUARIO_BADGE_HINT_VISIBLE_MS = 20_000;
const PRONTUARIO_HEADER_HINT_VISIBLE_MS = 15_000;
const LIVE_CAPTION_MAX_LINES = 3;

const SessionCallPageShellPrebuilt = ({
	viewerRole,
	viewerDisplayName,
	exitPath,
	setVideoCallHeaderConfig,
	loadCredentials = SessionService.getVideoRoom,
}: SessionCallPageShellProps) => {
	const { sessionId } = useParams<{ sessionId: string }>();
	const location = useLocation();
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const containerRef = useRef<HTMLDivElement>(null);
	const callFrameRef = useRef<DailyCall | null>(null);
	const destroyCallPromiseRef = useRef<Promise<void> | null>(null);
	const navigateHomeOnLeaveRef = useRef(true);
	const lastDailyErrorMessageRef = useRef<string | null>(null);
	const skipJoinSetupRef = useRef(false);
	const errorKindRef = useRef<SessionCallJoinErrorKind | null>(null);

	const [status, setStatus] = useState<'active' | 'error'>('active');
	const [errorKind, setErrorKind] = useState<SessionCallJoinErrorKind | null>(
		null,
	);
	const [errorMessage, setErrorMessage] = useState<string | null>(null);
	const [joinRetryNonce, setJoinRetryNonce] = useState(0);
	const [countdownNow, setCountdownNow] = useState(() => Date.now());
	errorKindRef.current = errorKind;
	const [professionalSessionEnded, setProfessionalSessionEnded] =
		useState(false);
	const [isPostSessionTransition, setIsPostSessionTransition] =
		useState(false);
	const [postSessionPhase, setPostSessionPhase] = useState<
		'transition' | 'review' | 'success'
	>('transition');
	const isPostSessionTransitionRef = useRef(false);
	const professionalSessionEndingRef = useRef(false);
	const prefetchStartedRef = useRef(false);
	isPostSessionTransitionRef.current = isPostSessionTransition;
	const [isEndSessionConfirmOpen, setIsEndSessionConfirmOpen] =
		useState(false);
	const [isProntuarioOpen, setIsProntuarioOpen] = useState(false);
	const [prontuarioPanelKey, setProntuarioPanelKey] = useState(0);
	const [prontuarioBadgeHint, setProntuarioBadgeHint] =
		useState<SessionCallProntuarioBadgeHint>('hidden');
	const [
		showProntuarioHeaderRecordingHint,
		setShowProntuarioHeaderRecordingHint,
	] = useState(false);
	const [prontuarioHeaderHintNonce, setProntuarioHeaderHintNonce] =
		useState(0);
	const hasOpenedProntuarioRef = useRef(false);
	const hasShownTimedProntuarioHintRef = useRef(false);
	const hasShownProntuarioHeaderRecordingHintRef = useRef(false);
	const [hasJoinedCall, setHasJoinedCall] = useState(false);
	const [isLiveCaptionsEnabled, setIsLiveCaptionsEnabled] = useState(false);
	const [liveCaptionLines, setLiveCaptionLines] = useState<string[]>([]);
	const [transcriptionHistory, setTranscriptionHistory] = useState<
		SessionCallTranscriptionChatMessage[]
	>([]);
	const [isProntuarioLiveCaptureEnabled, setIsProntuarioLiveCaptureEnabled] =
		useState(true);
	const [
		prontuarioTranscriptionStoppedNotice,
		setProntuarioTranscriptionStoppedNotice,
	] = useState<string | null>(null);
	const isLiveCaptionsEnabledRef = useRef(false);
	const isProntuarioLiveCaptureEnabledRef = useRef(true);
	const transcriptionFinalizeTimeoutRef = useRef<number | null>(null);

	const clearTranscriptionFinalizeTimeout = () => {
		if (transcriptionFinalizeTimeoutRef.current !== null) {
			window.clearTimeout(transcriptionFinalizeTimeoutRef.current);
			transcriptionFinalizeTimeoutRef.current = null;
		}
	};

	const scheduleTranscriptionFinalize = () => {
		clearTranscriptionFinalizeTimeout();
		transcriptionFinalizeTimeoutRef.current = window.setTimeout(() => {
			transcriptionFinalizeTimeoutRef.current = null;
			setTranscriptionHistory((current) =>
				finalizeLiveTranscriptionChatMessages(current),
			);
		}, TRANSCRIPTION_CHAT_GROUP_PAUSE_MS);
	};

	const accessState = isSessionCallAccessState(location.state)
		? location.state
		: null;
	const prefetchedCredentials = accessState?.credentials;
	const hasRoleMismatch =
		accessState !== null && accessState.viewerRole !== viewerRole;

	const { data: sessionData, isLoading: isSessionLoading } = useGetSession(
		sessionId ?? '',
		{
			enabled: !!sessionId && !hasRoleMismatch,
		},
	);

	const resolvedSessionStatus =
		accessState?.sessionStatus ?? sessionData?.status ?? null;

	const isReadyToJoin = useMemo(() => {
		if (viewerRole !== 'professional') return true;
		if (accessState?.sessionStatus != null) return true;
		return !isSessionLoading && sessionData?.status != null;
	}, [
		accessState?.sessionStatus,
		isSessionLoading,
		sessionData?.status,
		viewerRole,
	]);
	const showProfessionalEndSession = shouldShowProfessionalEndSession({
		viewerRole,
		sessionStatus: resolvedSessionStatus,
		professionalSessionEnded,
	});
	const sessionStartMs = sessionData?.selected_date
		? new Date(sessionData.selected_date).getTime()
		: null;
	const sessionStartLabel = sessionData?.selected_date
		? new Date(sessionData.selected_date).toLocaleString('pt-BR', {
				dateStyle: 'short',
				timeStyle: 'short',
		  })
		: null;
	const meetingStartsInLabel =
		errorKind === 'not-yet-available'
			? formatTimeUntil(
					sessionStartMs === null
						? null
						: sessionStartMs - countdownNow,
			  )
			: null;

	const sessionStartMsRef = useRef(sessionStartMs);
	const showProfessionalEndSessionRef = useRef(showProfessionalEndSession);
	sessionStartMsRef.current = sessionStartMs;
	showProfessionalEndSessionRef.current = showProfessionalEndSession;

	useEffect(() => {
		if (hasRoleMismatch) {
			navigate(exitPath, { replace: true });
		}
	}, [exitPath, hasRoleMismatch, navigate]);

	useEffect(() => {
		if (errorKind !== 'not-yet-available') {
			return;
		}

		setCountdownNow(Date.now());
		const intervalId = window.setInterval(() => {
			setCountdownNow(Date.now());
		}, 1000);

		return () => window.clearInterval(intervalId);
	}, [errorKind]);

	useEffect(() => {
		if (
			errorKind !== 'not-yet-available' ||
			!sessionId ||
			hasRoleMismatch
		) {
			return;
		}

		const triggerRetry = () => {
			setJoinRetryNonce((value) => value + 1);
		};

		if (sessionStartMs === null) {
			return;
		}

		const retryTimeoutMs =
			sessionStartMs > Date.now() ? sessionStartMs - Date.now() : null;
		if (retryTimeoutMs === null) {
			return;
		}

		const timeoutId = window.setTimeout(triggerRetry, retryTimeoutMs);

		return () => {
			window.clearTimeout(timeoutId);
		};
	}, [errorKind, hasRoleMismatch, sessionId, sessionStartMs]);

	useEffect(() => {
		if (errorKind !== 'room-not-ready' || !sessionId || hasRoleMismatch) {
			return;
		}

		const intervalId = window.setInterval(() => {
			setJoinRetryNonce((value) => value + 1);
		}, SESSION_CALL_VIDEO_ROOM_RETRY_MS);

		return () => {
			window.clearInterval(intervalId);
		};
	}, [errorKind, hasRoleMismatch, sessionId]);

	useEffect(() => {
		setProfessionalSessionEnded(false);
	}, [sessionId, viewerRole, accessState?.sessionStatus]);

	useEffect(() => {
		setIsProntuarioOpen(false);
		setHasJoinedCall(false);
		setIsPostSessionTransition(false);
		isPostSessionTransitionRef.current = false;
		professionalSessionEndingRef.current = false;
		hasOpenedProntuarioRef.current = false;
		hasShownTimedProntuarioHintRef.current = false;
		hasShownProntuarioHeaderRecordingHintRef.current = false;
		setProntuarioBadgeHint('hidden');
		setShowProntuarioHeaderRecordingHint(false);
		setProntuarioHeaderHintNonce(0);
		setIsLiveCaptionsEnabled(false);
		setLiveCaptionLines([]);
		clearTranscriptionFinalizeTimeout();
		setTranscriptionHistory([]);
		setIsProntuarioLiveCaptureEnabled(true);
		setProntuarioTranscriptionStoppedNotice(null);
	}, [sessionId]);

	useEffect(() => {
		isLiveCaptionsEnabledRef.current = isLiveCaptionsEnabled;
	}, [isLiveCaptionsEnabled]);

	useEffect(() => {
		isProntuarioLiveCaptureEnabledRef.current =
			isProntuarioLiveCaptureEnabled;
	}, [isProntuarioLiveCaptureEnabled]);

	const dismissProntuarioBadgeHint = useCallback(() => {
		setProntuarioBadgeHint('hidden');
	}, []);

	const dismissProntuarioHeaderRecordingHint = useCallback(() => {
		setShowProntuarioHeaderRecordingHint(false);
	}, []);

	const handleClearProntuarioTranscription = useCallback(() => {
		clearTranscriptionFinalizeTimeout();
		setTranscriptionHistory([]);
	}, []);

	const handleStopProntuarioLiveCapture = useCallback(() => {
		clearTranscriptionFinalizeTimeout();
		setTranscriptionHistory((current) =>
			finalizeLiveTranscriptionChatMessages(current),
		);
		setIsProntuarioLiveCaptureEnabled(false);
		setProntuarioTranscriptionStoppedNotice(
			PRONTUARIO_LIVE_CAPTURE_STOPPED_NOTICE,
		);
	}, []);

	const handleResumeProntuarioLiveCapture = useCallback(() => {
		setIsProntuarioLiveCaptureEnabled(true);
		setProntuarioTranscriptionStoppedNotice(null);
	}, []);

	const handleOpenProntuario = useCallback(() => {
		hasOpenedProntuarioRef.current = true;
		setProntuarioPanelKey((current) => current + 1);
		setIsProntuarioOpen(true);
		dismissProntuarioBadgeHint();
		if (!hasShownProntuarioHeaderRecordingHintRef.current) {
			hasShownProntuarioHeaderRecordingHintRef.current = true;
			setShowProntuarioHeaderRecordingHint(true);
		}
	}, [dismissProntuarioBadgeHint]);

	useEffect(() => {
		if (prontuarioBadgeHint === 'hidden') return;

		const timeoutId = window.setTimeout(
			dismissProntuarioBadgeHint,
			PRONTUARIO_BADGE_HINT_VISIBLE_MS,
		);
		return () => window.clearTimeout(timeoutId);
	}, [dismissProntuarioBadgeHint, prontuarioBadgeHint]);

	useEffect(() => {
		if (!showProntuarioHeaderRecordingHint) return;

		const timeoutId = window.setTimeout(
			dismissProntuarioHeaderRecordingHint,
			PRONTUARIO_HEADER_HINT_VISIBLE_MS,
		);
		return () => window.clearTimeout(timeoutId);
	}, [
		dismissProntuarioHeaderRecordingHint,
		prontuarioHeaderHintNonce,
		showProntuarioHeaderRecordingHint,
	]);

	const handleMinimizeProntuario = useCallback(() => {
		setIsProntuarioOpen(false);
		hasOpenedProntuarioRef.current = true;
		dismissProntuarioBadgeHint();
		dismissProntuarioHeaderRecordingHint();
	}, [dismissProntuarioBadgeHint, dismissProntuarioHeaderRecordingHint]);

	const handleToggleProntuario = useCallback(() => {
		if (isProntuarioOpen) {
			handleMinimizeProntuario();
			return;
		}
		handleOpenProntuario();
	}, [handleMinimizeProntuario, handleOpenProntuario, isProntuarioOpen]);

	const handleToggleProntuarioRef = useRef(handleToggleProntuario);
	handleToggleProntuarioRef.current = handleToggleProntuario;

	const handleToggleLiveCaptions = useCallback(() => {
		setIsLiveCaptionsEnabled((current) => {
			const next = !current;
			if (!next) {
				setLiveCaptionLines([]);
				return next;
			}

			if (isSessionTranscriptionAllowed(sessionData)) {
				ensureDailyCallTranscriptionStarted(callFrameRef.current);
			}

			return next;
		});
	}, [sessionData]);

	const handleToggleLiveCaptionsRef = useRef(handleToggleLiveCaptions);
	handleToggleLiveCaptionsRef.current = handleToggleLiveCaptions;

	const goBack = useCallback(() => {
		navigate(exitPath);
	}, [exitPath, navigate]);

	// Reinicia a fase pós-sessão quando a transição é desligada (defensivo).
	useEffect(() => {
		if (!isPostSessionTransition) setPostSessionPhase('transition');
	}, [isPostSessionTransition]);

	const handleViewProntuarioFromReview = useCallback(
		(options: {
			patientId: string;
			medicalRecordSessionId: string;
			platformSessionId: string;
		}) => {
			navigate(exitPath, { state: { openProntuario: options } });
		},
		[exitPath, navigate],
	);

	const prefetchPatientId = useMemo(
		() =>
			sessionData
				? resolveSessionParticipantTarget(sessionData).patientId ?? null
				: null,
		[sessionData],
	);

	const handlePostSessionTransitionComplete = useCallback(() => {
		const cachedSession = sessionId
			? queryClient.getQueryData<Sessions.Session>(['session', sessionId])
			: null;
		const cachedRecord = prefetchPatientId
			? queryClient.getQueryData<MedicalRecords.MedicalRecord>([
					'medical-record',
					prefetchPatientId,
			  ])
			: null;
		const recordSessionId =
			cachedSession?.medical_record_session?.id ?? null;
		const cachedSessionDetail =
			cachedRecord?.id && recordSessionId
				? queryClient.getQueryData<MedicalRecords.MedicalRecordSession>(
						[
							'medical-record-session',
							cachedRecord.id,
							recordSessionId,
							'post-process',
						],
				  )
				: null;

		console.log(
			'[prefetch-prontuario] review phase opening — cache snapshot',
			{
				sessionId,
				prefetchPatientId,
				hasCachedSession: !!cachedSession,
				recordSessionId,
				recordId: cachedRecord?.id ?? null,
				hasCachedSessionDetail: !!cachedSessionDetail,
				hasCachedRecord: !!cachedRecord,
				prefetchReady: !!(
					cachedRecord?.id &&
					recordSessionId &&
					cachedSessionDetail
				),
			},
		);

		setPostSessionPhase('review');
	}, [queryClient, sessionId, prefetchPatientId]);

	// Chamado no exato momento em que o pós-processamento da IA é concluído (is_done = true).
	// Invalida caches antigos e busca as 3 rotas enquanto a animação ainda está rodando,
	// para que a tela de revisão abra com os dados da IA já disponíveis.
	const handleProcessingDone = useCallback(() => {
		if (prefetchStartedRef.current) {
			console.log(
				'[prefetch-prontuario] handleProcessingDone — skipped (already started)',
			);
			return;
		}
		prefetchStartedRef.current = true;

		console.log('[prefetch-prontuario] handleProcessingDone — start', {
			prefetchPatientId,
			sessionId,
			staleMedicalRecordSession:
				sessionData?.medical_record_session ?? null,
		});

		if (!prefetchPatientId || !sessionId) {
			console.warn(
				'[prefetch-prontuario] handleProcessingDone — aborted (missing ids)',
				{
					prefetchPatientId,
					sessionId,
				},
			);
			return;
		}

		void queryClient.invalidateQueries({
			queryKey: ['medical-record', prefetchPatientId],
		});
		void queryClient.invalidateQueries({ queryKey: ['sessions', true] });
		void queryClient.invalidateQueries({
			queryKey: ['medical-record-session'],
		});

		void (async () => {
			const startedAt = performance.now();

			try {
				console.log(
					'[prefetch-prontuario] fetching routes 1+2 (parallel)',
					{
						medicalRecord: `GET /medical-records/patient/${prefetchPatientId}`,
						sessions: `GET /session/me?patient_id=${prefetchPatientId}&page=1&limit=50`,
					},
				);

				const [record, sessions] = await Promise.all([
					queryClient.fetchQuery({
						queryKey: ['medical-record', prefetchPatientId],
						queryFn: () =>
							MedicalRecordService.getByPatient(
								prefetchPatientId,
							),
						staleTime: 0,
					}),
					queryClient.fetchQuery({
						queryKey: [
							'sessions',
							true,
							undefined,
							undefined,
							undefined,
							undefined,
							prefetchPatientId,
							undefined,
							50,
							1,
							undefined,
							undefined,
						],
						queryFn: () =>
							SessionService.getMe({
								patient_id: prefetchPatientId,
								page: 1,
								limit: 50,
							}),
						staleTime: 0,
					}),
				]);

				console.log('[prefetch-prontuario] routes 1+2 — ok', {
					recordId: record?.id ?? null,
					sessionsTotal: sessions?.total ?? null,
					sessionsCount: sessions?.data?.length ?? 0,
				});

				const currentSession = sessions.data.find(
					(item) => item.id === sessionId,
				);
				const recordSessionId =
					currentSession?.medical_record_session?.id ?? null;

				console.log(
					'[prefetch-prontuario] resolved recordSessionId from sessions/me',
					{
						sessionFound: !!currentSession,
						recordSessionId,
						medicalRecordSession:
							currentSession?.medical_record_session ?? null,
					},
				);

				if (currentSession) {
					queryClient.setQueryData(
						['session', sessionId],
						currentSession,
					);
					console.log('[prefetch-prontuario] session cache updated', {
						sessionId,
					});
				} else {
					console.warn(
						'[prefetch-prontuario] session not found in sessions/me',
						{
							sessionId,
							availableSessionIds: sessions.data.map(
								(item) => item.id,
							),
						},
					);
				}

				if (!record?.id) {
					console.warn(
						'[prefetch-prontuario] prefetch incomplete — missing record.id',
						{ record },
					);
					return;
				}

				if (!recordSessionId) {
					console.warn(
						'[prefetch-prontuario] prefetch incomplete — missing recordSessionId',
						{
							currentSession,
						},
					);
					return;
				}

				console.log('[prefetch-prontuario] fetching route 3', {
					medicalRecordSession: `GET /medical-records/${record.id}/sessions/${recordSessionId}`,
				});

				const sessionDetail = await queryClient.fetchQuery({
					queryKey: [
						'medical-record-session',
						record.id,
						recordSessionId,
						'post-process',
					],
					queryFn: () =>
						MedicalRecordService.getSession(
							record.id,
							recordSessionId,
						),
					staleTime: 0,
				});

				const elapsedMs = Math.round(performance.now() - startedAt);

				console.log(
					'[prefetch-prontuario] prefetch complete — success',
					{
						elapsedMs,
						recordId: record.id,
						recordSessionId,
						sessionNumber: sessionDetail?.session_number ?? null,
						hasSummary: !!sessionDetail?.summary?.trim(),
						isFirstSession: (sessions?.total ?? 0) === 1,
					},
				);
			} catch (error) {
				const elapsedMs = Math.round(performance.now() - startedAt);
				console.error('[prefetch-prontuario] prefetch failed', {
					elapsedMs,
					error,
				});
			}
		})();
	}, [
		prefetchPatientId,
		sessionId,
		queryClient,
		sessionData?.medical_record_session,
	]);

	const destroyCurrentCall = useCallback(async () => {
		if (destroyCallPromiseRef.current) {
			await destroyCallPromiseRef.current;
			return;
		}

		const destroyPromise = (async () => {
			const call = callFrameRef.current ?? DailyIframe.getCallInstance();
			callFrameRef.current = null;

			if (!call || call.isDestroyed()) {
				return;
			}

			try {
				await call.destroy();
			} catch {
				/* cleanup defensivo */
			}
		})();

		destroyCallPromiseRef.current = destroyPromise;

		try {
			await destroyPromise;
		} finally {
			destroyCallPromiseRef.current = null;
		}
	}, []);

	useEffect(() => {
		if (!sessionId || hasRoleMismatch || !sessionData) {
			return;
		}

		// Durante o encerramento (ou transição de prontuário) não exibir "reunião indisponível".
		if (
			isPostSessionTransitionRef.current ||
			professionalSessionEndingRef.current
		) {
			return;
		}

		if (!isSessionCallPeriodElapsed(sessionData)) {
			return;
		}

		skipJoinSetupRef.current = true;
		navigateHomeOnLeaveRef.current = false;
		setStatus('error');
		setErrorKind('expired');
		setErrorMessage(SESSION_CALL_EXPIRED_MESSAGE);
		void destroyCurrentCall();
	}, [
		destroyCurrentCall,
		hasRoleMismatch,
		isPostSessionTransition,
		sessionData,
		sessionId,
	]);

	const showJoinError = useCallback(
		(error: unknown, fallbackMessage?: string | null) => {
			// A chamada é destruída de propósito ao encerrar a sessão; ignore erros
			// tardios do Daily para não piscar a tela de erro sob a transição.
			if (
				isPostSessionTransitionRef.current ||
				professionalSessionEndingRef.current
			) {
				return;
			}

			const structuredError =
				typeof error === 'object' &&
				error !== null &&
				'kind' in error &&
				'message' in error &&
				typeof (error as { kind?: unknown }).kind === 'string' &&
				typeof (error as { message?: unknown }).message === 'string'
					? (error as {
							kind: SessionCallJoinErrorKind;
							message: string;
					  })
					: null;
			const apiError = structuredError
				? null
				: resolveVideoRoomApiError(error);
			const errorDetails =
				structuredError ??
				apiError ??
				resolveSessionCallJoinError(error, fallbackMessage, {
					sessionStartMs: sessionStartMsRef.current,
				});
			if (!isSessionCallRetryableErrorKind(errorDetails.kind)) {
				skipJoinSetupRef.current = true;
			}
			navigateHomeOnLeaveRef.current = false;
			setStatus('error');
			setErrorKind(errorDetails.kind);
			setErrorMessage(errorDetails.message);
			setHasJoinedCall(false);
			setIsProntuarioOpen(false);
			void destroyCurrentCall();
		},
		[destroyCurrentCall],
	);

	const leaveAndDestroyCall = useCallback(async () => {
		const call = callFrameRef.current;
		if (!call || call.isDestroyed()) {
			return;
		}

		if (call.meetingState() === 'left-meeting') {
			await destroyCurrentCall();
			return;
		}

		navigateHomeOnLeaveRef.current = false;

		try {
			await Promise.race([
				call.leave(),
				new Promise((resolve) => window.setTimeout(resolve, 1200)),
			]);
		} catch {
			/* fallback abaixo */
		}

		await destroyCurrentCall();
	}, [destroyCurrentCall]);

	const requestLeaveCall = useCallback(async () => {
		await leaveAndDestroyCall();
		goBack();
	}, [goBack, leaveAndDestroyCall]);

	const handleProfessionalSessionEnded = useCallback(() => {
		professionalSessionEndingRef.current = true;
		isPostSessionTransitionRef.current = true;
		setProfessionalSessionEnded(true);
		setIsPostSessionTransition(true);
		setIsProntuarioOpen(false);
		setPostSessionPhase(
			isSessionTranscriptionAllowed(sessionData)
				? 'transition'
				: 'success',
		);
		void leaveAndDestroyCall();
	}, [leaveAndDestroyCall, sessionData]);

	const {
		mutate: endProfessionalSession,
		isPending: isEndingProfessionalSession,
	} = usePatchSessionEnd(handleProfessionalSessionEnded);

	const handleEndProfessionalSession = useCallback(() => {
		if (!sessionId || viewerRole !== 'professional') return;

		professionalSessionEndingRef.current = true;

		const rawNotes = peekProntuarioSessionNotes(
			sessionId,
			sessionData?.selected_date,
		);

		endProfessionalSession({
			id: sessionId,
			raw_notes: (rawNotes ?? '').trim() || undefined,
		});
	}, [
		endProfessionalSession,
		sessionData?.selected_date,
		sessionId,
		viewerRole,
	]);

	useEffect(() => {
		skipJoinSetupRef.current = false;
	}, [sessionId]);

	useEffect(() => {
		if (!sessionId || hasRoleMismatch || !isReadyToJoin) {
			return;
		}

		const isAutoRetryAttempt =
			joinRetryNonce > 0 &&
			isSessionCallRetryableErrorKind(errorKindRef.current);

		if (!isAutoRetryAttempt && skipJoinSetupRef.current) {
			return;
		}

		let cancelled = false;
		navigateHomeOnLeaveRef.current = true;
		setStatus('active');
		setErrorKind(null);
		setErrorMessage(null);
		const shouldShowEndSessionTrayButton =
			showProfessionalEndSessionRef.current;
		const resolvedSessionType =
			accessState?.sessionType ?? sessionData?.type ?? null;
		const resolvedIsOnline =
			accessState?.isOnline ?? sessionData?.is_online ?? false;
		const resolvedTranscriptionAllowed =
			accessState?.transcriptionAllowed ??
			isSessionTranscriptionAllowed(sessionData);
		const resolvedTranscriptionMock =
			isSessionTranscriptionMock(sessionData);
		const isProfessionalProntuarioSession =
			viewerRole === 'professional' &&
			isSessionEligibleForCallProntuario({
				sessionType: resolvedSessionType,
				isOnline: resolvedIsOnline,
				transcriptionAllowed: resolvedTranscriptionAllowed,
				transcriptionMock: resolvedTranscriptionMock,
			});
		const customTrayButtons =
			shouldShowEndSessionTrayButton || isProfessionalProntuarioSession
				? buildProfessionalTrayButtons({
						includeEndSession: shouldShowEndSessionTrayButton,
				  })
				: undefined;

		const setupCall = async () => {
			try {
				await destroyCurrentCall();
				if (cancelled) return;

				const container = containerRef.current;
				if (!container) {
					throw new Error('Container da chamada indisponível.');
				}

				const { url, token } =
					!isAutoRetryAttempt && prefetchedCredentials
						? prefetchedCredentials
						: await loadCredentials(sessionId);
				if (cancelled) return;

				container.replaceChildren();

				const callFrame = DailyIframe.createFrame(container, {
					showLeaveButton: !shouldShowEndSessionTrayButton,
					showFullscreenButton: true,
					...(customTrayButtons ? { customTrayButtons } : {}),
					iframeStyle: {
						position: 'absolute',
						top: '0',
						left: '0',
						width: '100%',
						height: '100%',
						border: '0',
					},
				});

				callFrameRef.current = callFrame;
				lastDailyErrorMessageRef.current = null;

				if (customTrayButtons || isProfessionalProntuarioSession) {
					callFrame.on('custom-button-click', (event) => {
						const buttonId = (event as { button_id?: string })
							.button_id;
						if (buttonId === PLUMAA_END_SESSION_TRAY_BUTTON_ID) {
							setIsEndSessionConfirmOpen(true);
							return;
						}
						if (buttonId === PLUMAA_PRONTUARIO_TRAY_BUTTON_ID) {
							handleToggleProntuarioRef.current();
							return;
						}
						if (buttonId === PLUMAA_LIVE_CAPTIONS_TRAY_BUTTON_ID) {
							handleToggleLiveCaptionsRef.current();
						}
					});
				}

				callFrame.on('joined-meeting', () => {
					if (!cancelled) {
						setHasJoinedCall(true);
						if (
							isProfessionalProntuarioSession ||
							shouldShowEndSessionTrayButton
						) {
							callFrame.loadCss({
								cssText: SESSION_CALL_TRAY_CUSTOM_CSS,
							});
						}
					}
				});

				callFrame.on('left-meeting', () => {
					setHasJoinedCall(false);
					setIsProntuarioOpen(false);
					setIsLiveCaptionsEnabled(false);
					setLiveCaptionLines([]);
					clearTranscriptionFinalizeTimeout();
					setTranscriptionHistory([]);
					if (!navigateHomeOnLeaveRef.current) return;
					void destroyCurrentCall().then(goBack);
				});

				callFrame.on('error', (event) => {
					const message =
						(
							event as {
								errorMsg?: string;
								error?: { msg?: string };
							}
						).errorMsg ??
						(event as { error?: { msg?: string } }).error?.msg ??
						null;
					lastDailyErrorMessageRef.current = message;
					if (cancelled || !shouldHandleCallJoinError(callFrame)) {
						return;
					}
					showJoinError(event, message);
				});

				if (viewerRole === 'professional' && !token) {
					throw new Error(
						'Token de sala ausente para o profissional.',
					);
				}

				await callFrame.join(
					buildDailyJoinProperties(url, token, viewerDisplayName),
				);

				if (!cancelled) {
					setStatus('active');
					setErrorKind(null);
					setErrorMessage(null);
					setHasJoinedCall(true);
				}
			} catch (error) {
				if (!cancelled) {
					setHasJoinedCall(false);
					showJoinError(error, lastDailyErrorMessageRef.current);
				}
			}
		};

		void setupCall();

		return () => {
			cancelled = true;
			navigateHomeOnLeaveRef.current = false;
			void destroyCurrentCall();
		};
	}, [
		destroyCurrentCall,
		goBack,
		hasRoleMismatch,
		isReadyToJoin,
		joinRetryNonce,
		loadCredentials,
		prefetchedCredentials,
		sessionId,
		showJoinError,
		viewerDisplayName,
	]);

	const handleBack = useCallback(() => {
		if (status === 'error') {
			goBack();
			return;
		}
		void requestLeaveCall();
	}, [goBack, requestLeaveCall, status]);

	const resolvedSessionType =
		accessState?.sessionType ?? sessionData?.type ?? null;
	const resolvedIsOnline =
		accessState?.isOnline ?? sessionData?.is_online ?? false;
	const resolvedTranscriptionMock = isSessionTranscriptionMock(sessionData);
	const resolvedTranscriptionAllowed =
		accessState?.transcriptionAllowed ??
		isSessionTranscriptionAllowed(sessionData);

	const showProfessionalProntuario = shouldShowSessionCallProntuario(
		viewerRole,
		{
			hasError: status === 'error',
			sessionType: resolvedSessionType,
			isOnline: resolvedIsOnline,
			transcriptionAllowed: resolvedTranscriptionAllowed,
			transcriptionMock: resolvedTranscriptionMock,
			hasJoinedCall,
		},
	);

	const sessionTranscriptionAllowed =
		isSessionTranscriptionAllowed(sessionData);
	const prontuarioBadgeHintMessage = sessionTranscriptionAllowed
		? PRONTUARIO_TRANSCRIPTION_BADGE_HINT
		: PRONTUARIO_NO_TRANSCRIPTION_BADGE_HINT;
	const prontuarioHeaderHintMessage = sessionTranscriptionAllowed
		? PRONTUARIO_TRANSCRIPTION_HEADER_HINT
		: PRONTUARIO_NO_TRANSCRIPTION_HEADER_HINT;

	useEffect(() => {
		const callFrame = callFrameRef.current;
		if (!callFrame || viewerRole !== 'professional') return;

		callFrame.updateCustomTrayButtons(
			buildProfessionalTrayButtons({
				// includeLiveCaptions: showProfessionalProntuario,
				includeProntuario: showProfessionalProntuario,
				includeEndSession: showProfessionalEndSession,
				// isLiveCaptionsEnabled,
				isProntuarioOpen,
			}),
		);

		if (!showProfessionalProntuario && !showProfessionalEndSession) return;

		callFrame.loadCss({
			cssText: SESSION_CALL_TRAY_CUSTOM_CSS,
		});
	}, [
		hasJoinedCall,
		isLiveCaptionsEnabled,
		isProntuarioOpen,
		showProfessionalEndSession,
		showProfessionalProntuario,
		viewerRole,
	]);

	useEffect(() => {
		const callFrame = callFrameRef.current;
		if (
			!callFrame ||
			callFrame.isDestroyed() ||
			!hasJoinedCall ||
			viewerRole !== 'professional' ||
			!showProfessionalProntuario
		) {
			return;
		}

		const handleTranscriptionMessage = (
			event: DailyEventObjectTranscriptionMessage,
		) => {
			const message = createTranscriptionChatMessage(callFrame, event);
			if (message && isProntuarioLiveCaptureEnabledRef.current) {
				setTranscriptionHistory((current) =>
					appendTranscriptionChatMessage(
						current,
						message,
						Date.now(),
					),
				);
				scheduleTranscriptionFinalize();
			}

			if (!isLiveCaptionsEnabledRef.current) return;

			const text = event.text?.trim();
			if (!text) return;

			setLiveCaptionLines((current) => {
				const next = [...current, text];
				return next.length > LIVE_CAPTION_MAX_LINES
					? next.slice(-LIVE_CAPTION_MAX_LINES)
					: next;
			});
		};

		callFrame.on('transcription-message', handleTranscriptionMessage);

		return () => {
			clearTranscriptionFinalizeTimeout();
			if (!callFrame.isDestroyed()) {
				callFrame.off(
					'transcription-message',
					handleTranscriptionMessage,
				);
			}
		};
	}, [hasJoinedCall, joinRetryNonce, showProfessionalProntuario, viewerRole]);

	useEffect(() => {
		if (
			viewerRole !== 'professional' ||
			!hasJoinedCall ||
			!sessionTranscriptionAllowed
		) {
			return;
		}

		ensureDailyCallTranscriptionStarted(callFrameRef.current);
	}, [
		hasJoinedCall,
		joinRetryNonce,
		sessionTranscriptionAllowed,
		viewerRole,
	]);

	useEffect(() => {
		if (
			!showProfessionalProntuario ||
			!hasJoinedCall ||
			isProntuarioOpen ||
			hasOpenedProntuarioRef.current ||
			hasShownTimedProntuarioHintRef.current
		) {
			return;
		}

		hasShownTimedProntuarioHintRef.current = true;
		setProntuarioBadgeHint('initial');
	}, [hasJoinedCall, isProntuarioOpen, showProfessionalProntuario]);

	useLayoutEffect(() => {
		const showMeetingTimer =
			viewerRole === 'professional' &&
			sessionStartMs !== null &&
			!isPostSessionTransition &&
			!professionalSessionEnded;

		setVideoCallHeaderConfig({
			onBack: handleBack,
			onToggleLayout: () => undefined,
			activeSpeakerMode: true,
			controlsEnabled: false,
			showLayoutToggle: false,
			hideBackButton:
				isPostSessionTransition &&
				(postSessionPhase === 'transition' ||
					postSessionPhase === 'review'),
			meetingScheduledStartMs: showMeetingTimer ? sessionStartMs : null,
		});

		return () => setVideoCallHeaderConfig(null);
	}, [
		handleBack,
		isPostSessionTransition,
		postSessionPhase,
		professionalSessionEnded,
		sessionStartMs,
		setVideoCallHeaderConfig,
		viewerRole,
	]);

	if (!sessionId || hasRoleMismatch) {
		return null;
	}

	const isProntuarioSplitOpen =
		isProntuarioOpen && showProfessionalProntuario && status !== 'error';

	const hideSessionUnavailable =
		isPostSessionTransition ||
		professionalSessionEnded ||
		isEndingProfessionalSession;

	return (
		<div
			className={cn(
				'relative flex h-full min-h-0 w-full flex-col overflow-hidden',
				showProfessionalProntuario && PRONTUARIO_BG_CLASS,
			)}
		>
			{status === 'error' && !hideSessionUnavailable && (
				<div className="absolute inset-0 z-40 flex flex-col items-center justify-center gap-4 bg-white px-6 text-center text-zinc-700">
					<p className="max-w-md text-base font-medium leading-relaxed">
						{errorMessage}
					</p>
					{errorKind === 'access-denied' && (
						<p className="max-w-md text-sm leading-relaxed text-zinc-500">
							O participante não foi admitido na consulta.
						</p>
					)}
					{errorKind === 'room-not-ready' && (
						<p className="max-w-md text-sm leading-relaxed text-zinc-500">
							Estamos tentando conectar automaticamente. Isso pode
							levar alguns instantes após a confirmação da sessão.
						</p>
					)}
					{errorKind === 'not-yet-available' && (
						<div className="flex max-w-md flex-col gap-2 text-sm leading-relaxed text-zinc-500">
							{sessionStartLabel ? (
								<p>
									Horário agendado:{' '}
									<span className="font-semibold text-zinc-700">
										{sessionStartLabel}
									</span>
								</p>
							) : null}
							{meetingStartsInLabel ? (
								<p>
									Sua reunião começa em{' '}
									<span className="font-semibold text-zinc-700">
										{meetingStartsInLabel}
									</span>
									. Entraremos automaticamente quando a sala
									liberar.
								</p>
							) : (
								<p>
									Entraremos automaticamente assim que a sala
									for liberada.
								</p>
							)}
						</div>
					)}
					{errorKind === 'expired' && (
						<div className="flex max-w-md flex-col gap-2 text-sm leading-relaxed text-zinc-500">
							{sessionStartLabel ? (
								<p>
									Horário agendado:{' '}
									<span className="font-semibold text-zinc-700">
										{sessionStartLabel}
									</span>
								</p>
							) : null}
							<p>
								A sala de vídeo já foi encerrada e não aceita
								mais entradas. Volte ao início para ver o status
								da consulta.
							</p>
						</div>
					)}
					<button
						type="button"
						onClick={goBack}
						className="rounded-full border-2 border-orange-500 px-5 py-2.5 text-base font-bold text-orange-600 transition-colors hover:bg-orange-50"
					>
						Voltar
					</button>
				</div>
			)}

			{(status !== 'error' || hideSessionUnavailable) && (
				<SessionCallResizableSplit
					callContainerRef={containerRef}
					callOverlay={
						showProfessionalProntuario && isLiveCaptionsEnabled ? (
							<SessionCallLiveCaptionsOverlay
								lines={liveCaptionLines}
							/>
						) : null
					}
					prontuarioOpen={isProntuarioSplitOpen}
					prontuarioEnabled={showProfessionalProntuario}
					showProntuarioBadge={
						showProfessionalProntuario && !isProntuarioOpen
					}
					onOpenProntuario={handleOpenProntuario}
					prontuarioBadgeHint={prontuarioBadgeHint}
					prontuarioBadgeHintMessage={prontuarioBadgeHintMessage}
					transcriptionActive={sessionTranscriptionAllowed}
					onDismissProntuarioBadgeHint={dismissProntuarioBadgeHint}
					prontuarioPane={
						<SessionCallProntuarioPanel
							key={prontuarioPanelKey}
							session={sessionData}
							className="h-full w-full"
							onMinimize={handleMinimizeProntuario}
							showHeaderRecordingHint={
								showProntuarioHeaderRecordingHint
							}
							recordingHintMessage={prontuarioHeaderHintMessage}
							recordingHintKey={prontuarioHeaderHintNonce}
							transcriptionActive={sessionTranscriptionAllowed}
							isProntuarioLiveCaptureEnabled={
								isProntuarioLiveCaptureEnabled
							}
							prontuarioTranscriptionStoppedNotice={
								prontuarioTranscriptionStoppedNotice
							}
							onDismissHeaderRecordingHint={
								dismissProntuarioHeaderRecordingHint
							}
							transcriptionMessages={transcriptionHistory}
							onClearProntuarioTranscription={
								handleClearProntuarioTranscription
							}
							onStopProntuarioLiveCapture={
								handleStopProntuarioLiveCapture
							}
							onResumeProntuarioLiveCapture={
								handleResumeProntuarioLiveCapture
							}
						/>
					}
				/>
			)}

			{isPostSessionTransition &&
				postSessionPhase === 'transition' &&
				sessionId && (
					<SessionEndTransition
						sessionId={sessionId}
						onComplete={handlePostSessionTransitionComplete}
						onProcessingDone={handleProcessingDone}
					/>
				)}

			{isPostSessionTransition &&
				postSessionPhase === 'review' &&
				sessionData && (
					<SessionEndProntuarioReview
						session={sessionData}
						onGoHome={goBack}
						onViewProntuario={handleViewProntuarioFromReview}
					/>
				)}

			{isPostSessionTransition && postSessionPhase === 'success' && (
				<SessionEndCallSuccess onGoHome={goBack} />
			)}

			<ConfirmActionModal
				isOpen={isEndSessionConfirmOpen}
				onClose={() => setIsEndSessionConfirmOpen(false)}
				onConfirm={() => {
					setIsEndSessionConfirmOpen(false);
					handleEndProfessionalSession();
				}}
				title="Encerrar reunião"
				description="Você tem certeza que deseja encerrar a reunião?"
				confirmButtonText={
					isEndingProfessionalSession
						? 'Encerrando...'
						: 'Encerrar reunião'
				}
				disableConfirm={isEndingProfessionalSession}
				tone="neutral"
			/>
		</div>
	);
};

export { SessionCallPageShellPrebuilt };
